import { HttpStatusCode } from '@scayle/storefront-nuxt'
import { resolveRedirectTarget, type RedirectLink } from '../utils/storyblok'

/**
 * ECOM-2385: page-level redirect guard. Reads a page's `redirectUrl` settings
 * field and, when it points somewhere, issues an SSR 302 ("temporary moved")
 * redirect that also performs a client-side `router.replace` (no new history
 * entry). Internal targets navigate within the SPA; external `http(s)` targets
 * redirect away. The redirect decision (including scheme validation and the
 * self-loop guard) lives in the pure `resolveRedirectTarget` helper.
 *
 * `redirectIfNeeded` resolves to `true` when it triggered a redirect, so the
 * caller can `await` it and early-return — skipping rendering, the schedule
 * guard, and the 404 path. It awaits `navigateTo` internally: Nuxt requires the
 * `navigateTo` result to be awaited/returned from setup so the SSR redirect
 * aborts rendering; a fire-and-forget call can emit the full page HTML
 * alongside the 302.
 */
export function useStoryRedirect() {
  const i18n = useI18n()
  const route = useRoute()

  const redirectIfNeeded = async (
    redirectUrl?: RedirectLink,
  ): Promise<boolean> => {
    const decision = resolveRedirectTarget(
      redirectUrl,
      i18n.localeProperties.value.code,
      route.path,
    )

    if (decision.kind === 'none') {
      return false
    }

    // R9: never route-cache a redirect response. A cached 302 would be served to
    // every visitor for the route's max-age and keep redirecting after an editor
    // clears the field. Route cache only exists server-side.
    if (import.meta.server) {
      useRouteCache((helper) => helper.setUncacheable())
    }

    if (decision.kind === 'internal') {
      await navigateTo(decision.path, {
        redirectCode: HttpStatusCode.FOUND,
        replace: true,
      })
    } else {
      await navigateTo(decision.url, {
        redirectCode: HttpStatusCode.FOUND,
        external: true,
      })
    }

    return true
  }

  return { redirectIfNeeded }
}
